/** @jsx jsx */
import React from 'react'
import { css, jsx } from '@emotion/react'
import { Icon } from '../../utils/Icon'
import {
  FacilityAcceptanceStatus,
  FacilityAvailableStatusCode,
  ContractStatusType,
  FacilityAcceptanceText,
  FacilityPhoneSupportText,
} from '../../../packs/entites/FacilityInfo'
import {
  Card,
  CardHero,
  CardHeader,
  CardContent,
  CardFooterContracted,
  CardFooterNotContracted,
} from '../FacilityCard'

export type MapFacilityInfoProps = {
  id: string
  name: string
  contractStatus: ContractStatusType
  phoneNumber: string
  showPath?: string
  imageUrl: string
  siteUrl: string
  onClose: VoidFunction
  todayStatusCode: FacilityAvailableStatusCode
  todayStatusText: string
  tomorrowStatusCode: FacilityAvailableStatusCode
  tomorrowStatusText: string
  distance?: any
  isSignedIn: boolean
  googleMapUrl?: string
  latitude: number
  longitude: number
  bookmarkId: string | null
  bookmarkModalShowEnabled: boolean
  openingTimes: string[]
  usageBusinessDayText: string
  handleBookmark: VoidFunction
  handleUnbookmark: VoidFunction
  acceptanceTimeText: FacilityAcceptanceText | null
  acceptanceStatus: FacilityAcceptanceStatus
  acceptancePhoneTimeText: FacilityPhoneSupportText | null
  acceptancePhoneStatus: FacilityAcceptanceStatus
  nextWorkDayAcceptancePhoneTimeText: FacilityPhoneSupportText | null
  isVisualizationGroupFacility: boolean
  useReservationEnable: boolean
  useRegistrationEnable: boolean
  useReservationStartDate: string | null
  searchToday?: boolean
  attachedClinicName: string | null
  detailPublished: boolean | null
}

export const MapFacilityInfo = ({
  contractStatus,
  phoneNumber,
  id,
  name,
  showPath,
  imageUrl,
  siteUrl,
  onClose,
  todayStatusCode,
  todayStatusText,
  tomorrowStatusCode,
  tomorrowStatusText,
  distance,
  isSignedIn,
  googleMapUrl,
  latitude,
  longitude,
  bookmarkId,
  bookmarkModalShowEnabled,
  openingTimes,
  usageBusinessDayText,
  handleBookmark,
  handleUnbookmark,
  acceptanceTimeText,
  acceptanceStatus,
  acceptancePhoneTimeText,
  acceptancePhoneStatus,
  isVisualizationGroupFacility,
  useReservationEnable,
  useRegistrationEnable,
  useReservationStartDate,
  nextWorkDayAcceptancePhoneTimeText,
  searchToday = true,
  attachedClinicName,
  detailPublished,
}: MapFacilityInfoProps) => {
  return (
    <div css={rootStyle}>
      <CloseButton onClose={onClose} />
      <Card>
        <CardHero
          id={id}
          imageUrl={imageUrl}
          isSignedIn={isSignedIn}
          bookmarkId={bookmarkId}
          bookmarkModalShowEnabled={bookmarkModalShowEnabled}
          handleBookmark={handleBookmark}
          handleUnbookmark={handleUnbookmark}
          href={siteUrl}
        />
        <CardHeader
          name={name}
          href={siteUrl}
          attachedClinicName={attachedClinicName}
          detailPublish={detailPublished}
        />
        <CardContent
          todayStatusCode={todayStatusCode}
          todayStatusText={todayStatusText}
          tomorrowStatusCode={tomorrowStatusCode}
          tomorrowStatusText={tomorrowStatusText}
          distance={distance}
          isSignedIn={isSignedIn}
          googleMapUrl={googleMapUrl}
          latitude={latitude}
          longitude={longitude}
          openingTimes={openingTimes}
          contractStatus={contractStatus}
          isVisualizationGroupFacility={isVisualizationGroupFacility}
          useReservationEnable={useReservationEnable}
          bold={searchToday ? 'today' : 'tomorrow'}
        />

        {contractStatus === 'contracted' ? (
          <CardFooterContracted
            id={id}
            showPath={showPath}
            acceptanceTimeText={acceptanceTimeText}
            acceptancePhoneTimeText={
              acceptancePhoneStatus === 'end_acceptance' ||
              acceptancePhoneStatus === 'holiday'
                ? nextWorkDayAcceptancePhoneTimeText
                : acceptancePhoneTimeText
            }
            acceptanceStatus={acceptanceStatus}
            phoneNumber={phoneNumber}
            isVisualizationGroupFacility={isVisualizationGroupFacility}
            useRegistrationEnable={useRegistrationEnable}
            useReservationEnable={useReservationEnable}
            useReservationStartDate={useReservationStartDate}
            acceptancePhoneStatus={acceptancePhoneStatus}
          />
        ) : (
          <CardFooterNotContracted
            phoneNumber={phoneNumber}
            acceptancePhoneTimeText={
              acceptancePhoneStatus === 'end_acceptance' ||
              acceptancePhoneStatus === 'holiday'
                ? nextWorkDayAcceptancePhoneTimeText
                : acceptancePhoneTimeText
            }
            acceptanceStatus={
              acceptancePhoneStatus ? acceptancePhoneStatus : acceptanceStatus
            }
            isVisualizationGroupFacility={isVisualizationGroupFacility}
          />
        )}
      </Card>
      <Triangle />
    </div>
  )
}

const rootStyle = css`
  position: relative;
  max-width: 450px;
  min-width: 300px;
  margin: auto;
  box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.1);
`

const CloseButton = (props: { onClose: VoidFunction }) => {
  return (
    <span
      role="button"
      aria-label="閉じる"
      css={closeButtonStyle}
      onClick={props.onClose}
    >
      <Icon iconName="close-circle" />
    </span>
  )
}

const closeButtonStyle = css`
  z-index: 1;
  position: absolute;
  top: 0;
  right: 0;
  padding: 6px;
  cursor: pointer;
  transform: translate(14px, -14px);
  & > svg {
    width: 26px;
    height: 26px;
    display: block;
  }
`

const Triangle = () => {
  return <div css={triangleStyle}></div>
}

const triangleStyle = css`
  position: absolute;
  bottom: 1px;
  left: 50%;
  transform: translate(-50%, 100%);
  background-color: white;
  width: 22px;
  height: 15px;
  clip-path: polygon(0 0, 100% 0%, 50% 100%);
`
